import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import dayjs from "dayjs";

// Placeholder mapping for assignment data
const PLACEHOLDER_MAP = {
  ClientName: "Client?.Account",
  ClientAddress: "Client?.Address",
  LinguistName: "Linguist?.ShortName||Linguist?.Name",
  LinguistNumber: "Linguist?.PhoneNumber",
  LinguistAddress: "Linguist?.Address",
  AssignmentName: "Name",
  AssignmentDuration: "Duration",
  AssignmentStartTime: "TimeStart",
  AssignmentEndTime: "TimeEnd",
  AssignmentDate: "Date",
  AssignmentDetails: "Details||Name",
  AssignmentRequestor: "Requestor?.Name",
  // Multi-contact person support
  AssignmentContactPerson: "ContactPersons.0.Name||ContactPersonName",
  AssignmentContactPersonNumber: "ContactPersons.0.PhoneNumber||ContactPersonNumber",
  AssignmentContactPersonEmail: "ContactPersons.0.Email",
  AssignmentContactPersonExt: "ContactPersons.0.Ext",
  AssignmentType: "ServiceSubtype?.Name||InterpretationType",
  AssignmentLanguage: "ServiceTargetLanguages.0.Language.Name",
  AssignmentAddressLink: "Address",
  //aaa: "SchedulingTime",
  AssignmentPrepTime: "PreparationTime",
  Default: "NO_DATA_REPLACE_MANUALLY",
};

const formatTimeToHHMM12Hr = (timeStr) => {
  if (!timeStr) return "";
  // Handle format "HH:MM:SS" or "HH:MM"
  const timeParts = timeStr.split(":");
  if (!timeParts[0] || !timeParts[1]) return timeStr;

  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${ampm}`;
};

const formatDurationToHHMM = (durationStr) => {
  if (!durationStr) return "";
  // Handle format "HH:MM:SS" or "HH:MM"
  const parts = durationStr.split(":");
  if (!parts[0] || !parts[1]) return durationStr;

  return `${parts[0]}:${parts[1]}`;
};

const replacePlaceholders = (text, assignmentData) => {
  if (!text) return "";

  let result = text;
  const placeholderRegex = /\{([^}]+)\}/g;

  result = result.replace(placeholderRegex, (match, placeholder) => {
    // Handle nested properties (e.g., Client?.Account)
    const path = PLACEHOLDER_MAP[placeholder];
    if (!path) return match; // Return original if no mapping

    // Special handling for AssignmentContactPerson with multiple contacts
    if (placeholder === "AssignmentContactPerson") {
      const contactPersons = assignmentData?.ContactPersons || [];
      if (contactPersons.length > 1) {
        // Format all contact persons when there are multiple
        const formatted = contactPersons
          .map(
            (cp, i) =>
              `${i + 1}. ${cp.Name}${cp.PhoneNumber ? ` (${cp.PhoneNumber}${cp.Ext ? ` ext. ${cp.Ext}` : ""})` : ""}`
          )
          .join("<br/>");
        return formatted;
      }
      // Fall through to normal path resolution for single contact
    }

    const pathOptions = path.split("||").map((p) => p.trim());
    let value = null;

    for (const option of pathOptions) {
      const keys = option.split("?").join(".").split(".");
      let currentValue = assignmentData;

      for (const key of keys) {
        if (key && currentValue) {
          currentValue = currentValue[key];
        }
      }

      if (currentValue) {
        value = currentValue;
        break;
      }
    }

    if (!value)
      return '<strong style="color: red;">NO_DATA_REPLACE_MANUALLY</strong>';

    // Apply special formatting for time fields
    if (
      placeholder === "AssignmentStartTime" ||
      placeholder === "AssignmentEndTime"
    ) {
      return formatTimeToHHMM12Hr(value);
    }

    if (placeholder === "AssignmentDuration") {
      return formatDurationToHHMM(value);
    }

    // Apply special formatting for date field
    if (placeholder === "AssignmentDate") {
      return dayjs.utc(value).format("dddd, MMMM DD, YYYY");
    }

    return (
      value || '<strong style="color: red;">NO_DATA_REPLACE_MANUALLY</strong>'
    );
  });

  return result;
};

const htmlToPlainText = (html) => {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  container.querySelectorAll("br").forEach((node) => {
    node.replaceWith("\n");
  });

  container.querySelectorAll("p, div, li, tr").forEach((node) => {
    if (node.textContent?.trim()) {
      node.appendChild(document.createTextNode("\n"));
    }
  });

  return (container.innerText || container.textContent || "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const copyElementContents = (element) => {
  if (!element) return false;

  const selection = window.getSelection();
  if (!selection) return false;

  const previousRanges = [];
  for (let i = 0; i < selection.rangeCount; i += 1) {
    previousRanges.push(selection.getRangeAt(i).cloneRange());
  }

  const range = document.createRange();
  range.selectNodeContents(element);

  selection.removeAllRanges();
  selection.addRange(range);

  let didCopy = false;
  try {
    didCopy = document.execCommand("copy");
  } finally {
    selection.removeAllRanges();
    previousRanges.forEach((savedRange) => selection.addRange(savedRange));
  }

  return didCopy;
};

const InterpretationSendDetails = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar, openDialog, closeDialog } =
    useStateContext();
  const editorRef = useRef(null);

  const assignmentData = useMemo(
    () => overlayData?.Assignment || {},
    [overlayData?.Assignment],
  );

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [modifiers, setModifiers] = useState([]);

  const linguistEmail =
    assignmentData?.Linguist?.Email ||
    assignmentData?.Linguist?.EmailAddress ||
    assignmentData?.Linguist?.PersonalEmail ||
    "";

  const loadTemplateData = useCallback(
    (template) => {
      const replacedSubject = replacePlaceholders(
        template.Subject,
        assignmentData,
      );
      const replacedBody = replacePlaceholders(template.Body, assignmentData);

      setSubject(replacedSubject);
      setBodyHtml(replacedBody);

      if (editorRef.current) {
        editorRef.current.innerHTML = replacedBody;
      }
    },
    [assignmentData],
  );

  // Fetch email templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const [templatesResponse, modifiersResponse] = await Promise.all([
          api.get("/communicationTemplates?channel=Email&type=Template"),
          api.get("/communicationTemplates?channel=Email&type=Modifier"),
        ]);

        const templates = templatesResponse.data || [];
        setEmailTemplates(templates);
        setModifiers(modifiersResponse.data || []);

        const account = (assignmentData?.Client?.Account || "").toLowerCase();
        const interpretationType = (
          assignmentData?.ServiceSubtype?.Name || ""
        ).toLowerCase();
        const serviceName = (assignmentData?.Name || "").toLowerCase();

        const findTemplateByName = (needle) =>
          templates.find((t) => (t.Name || "").toLowerCase().includes(needle));

        let selectedTemplate = null;

        if (account === "global worldsmiths") {
          selectedTemplate = findTemplateByName("global worldsmiths");
        } else if (
          !selectedTemplate &&
          interpretationType.includes("virtual")
        ) {
          selectedTemplate = findTemplateByName("virtual");
        } else if (!selectedTemplate && interpretationType.includes("phone")) {
          selectedTemplate = findTemplateByName("phone");
        } else if (
          !selectedTemplate &&
          interpretationType.includes("in-person")
        ) {
          if (
            serviceName.includes("home") ||
            serviceName.includes("family visit")
          ) {
            selectedTemplate = findTemplateByName("home/family visit");
          } else {
            selectedTemplate = findTemplateByName("in-person");
          }
        }

        if (!selectedTemplate) {
          selectedTemplate = templates[0];
        }

        if (selectedTemplate) {
          setSelectedTemplateId(selectedTemplate.id);
          loadTemplateData(selectedTemplate);
        }
      } catch (err) {
        console.error("Error fetching email templates:", err);
        showSnackbar("Error loading email templates", theme, "error");
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [
    assignmentData?.Client?.Account,
    assignmentData?.ServiceSubtype?.Name,
    assignmentData?.Name,
    loadTemplateData,
    showSnackbar,
    theme,
  ]);

  const handleTemplateChange = (event) => {
    const templateId = event.target.value;
    setSelectedTemplateId(templateId);

    const selectedTemplate = emailTemplates.find((t) => t.id === templateId);
    if (selectedTemplate) {
      loadTemplateData(selectedTemplate);
    }
  };

  const handleBodyInput = (e) => {
    setBodyHtml(e.currentTarget.innerHTML);
  };

  const [savedRange, setSavedRange] = useState(null);

  const handleModifierMouseDown = (e) => {
    e.preventDefault();
    // Save the current selection before the click
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0));
    }
  };

  const insertModifier = (modifier) => {
    const el = editorRef.current;
    if (!el) return;

    // Focus the editor
    el.focus();

    // Restore the saved selection if available
    if (savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange);
      setSavedRange(null);
    }

    // Insert at cursor position using execCommand
    document.execCommand("insertHTML", false, modifier.Body);

    // Re-run placeholder replacement on the updated content
    const updatedContent = el.innerHTML;
    const replacedContent = replacePlaceholders(updatedContent, assignmentData);
    el.innerHTML = replacedContent;

    setBodyHtml(el.innerHTML);
  };

  const closeOverlay = () => setOverlayName("");

  const copyToClipboard = async ({ plainText, html, successMessage }) => {
    try {
      if (navigator.clipboard?.write && window.ClipboardItem && html) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = plainText;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      showSnackbar(successMessage, theme, "success");
    } catch (error) {
      console.error("Error copying content:", error);
      showSnackbar("Unable to copy content", theme, "error");
    }
  };

  const handleCopySubject = async () => {
    const plainText = (subject || "").trim();

    if (!plainText) {
      showSnackbar("Nothing to copy", theme, "warning");
      return;
    }

    await copyToClipboard({
      plainText,
      successMessage: "Subject copied",
    });
  };

  const handleCopyBody = async () => {
    const plainText = htmlToPlainText(bodyHtml);
    const html = bodyHtml?.trim();

    if (!plainText) {
      showSnackbar("Nothing to copy", theme, "warning");
      return;
    }

    if (copyElementContents(editorRef.current)) {
      showSnackbar("Body copied", theme, "success");
      return;
    }

    await copyToClipboard({
      plainText,
      html,
      successMessage: "Body copied",
    });
  };

  const handleCopyLinguistEmail = async () => {
    const plainText = (linguistEmail || "").trim();

    if (!plainText) {
      showSnackbar("No linguist email found", theme, "warning");
      return;
    }

    await copyToClipboard({
      plainText,
      successMessage: "Linguist email copied",
    });
  };

  const handleCopyEmailPackage = async () => {
    // Check if there are any unhandled manual replacements
    if (bodyHtml.includes("NO_DATA_REPLACE_MANUALLY")) {
      const count = (bodyHtml.match(/NO_DATA_REPLACE_MANUALLY/g) || []).length;

      openDialog({
        theme: theme,
        title: "Manual Replacements Required",
        message: `Can't send until all ${count} Manual replacement${count !== 1 ? "s" : ""} ${count !== 1 ? "are" : "is"} handled`,
        customContent: null,
        tertiaryAction: null,
        secondaryAction: "Close",
        primaryAction: null,
        tertiaryOnClick: null,
        secondaryOnClick: () => {
          closeDialog();
        },
        primaryOnClick: null,
      });
      return;
    }

    const packageParts = [];

    if (linguistEmail.trim()) {
      packageParts.push(`To: ${linguistEmail.trim()}`);
    }

    packageParts.push(`Subject: ${(subject || "").trim()}`);
    packageParts.push("");
    packageParts.push(htmlToPlainText(bodyHtml));

    await copyToClipboard({
      plainText: packageParts.join("\n"),
      successMessage: "Email package copied",
    });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          maxHeight: "90%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Send Details
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Copy package" placement="top">
                  <IconButton
                    onClick={handleCopyEmailPackage}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    <ContentCopyRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<ContentCopyRoundedIcon />}
                  onClick={handleCopyEmailPackage}
                  sx={{ borderRadius: "12px" }}
                >
                  Copy package
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Email Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Copy the recipient, subject, and body into your mail client. Manual replacements must be resolved before the package can be copied.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ maxLength: 255 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy" placement="top">
                        <IconButton edge="end" size="small" onClick={handleCopySubject}>
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Linguist Email"
                value={linguistEmail}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy" placement="top">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={handleCopyLinguistEmail}
                          disabled={!linguistEmail}
                        >
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Body
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <Tooltip title="Copy" placement="top">
                      <IconButton
                        size="small"
                        onClick={handleCopyBody}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          zIndex: 1,
                          border: `1px solid ${theme.palette.primary.border2}`,
                          "&:hover": {
                            bgcolor: theme.palette.primary.light,
                          },
                        }}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Box
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleBodyInput}
                      sx={{
                        boxSizing: "border-box",
                        height: 340,
                        maxHeight: 340,
                        p: 1.5,
                        pt: 6,
                        pr: 6,
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.primary.border2}`,
                        bgcolor: theme.palette.background.default,
                        outline: "none",
                        "&:focus": {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
                        },
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        "& > *:first-of-type": {
                          mt: "0 !important",
                        },
                        "& p, & div, & ul, & ol": {
                          mt: 0,
                          mb: 1,
                        },
                        "& p:last-child, & div:last-child, & ul:last-child, & ol:last-child": {
                          mb: 0,
                        },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Template
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      value={selectedTemplateId || ""}
                      onChange={handleTemplateChange}
                      label="Template"
                      size="small"
                      disabled={loadingTemplates}
                    >
                      {emailTemplates
                        .filter((t) => t.Type === "Template" || !t.Type)
                        .map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.Name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Modifiers
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      bgcolor: theme.palette.background.default,
                      height: 265,
                      maxHeight: 265,
                      overflowY: "auto",
                    }}
                  >
                    {modifiers.length > 0 ? (
                      modifiers
                        .filter((t) => t.Type === "Modifier")
                        .map((modifier) => (
                          <Box
                            key={modifier.id}
                            onMouseDown={handleModifierMouseDown}
                            onClick={() => insertModifier(modifier)}
                            sx={{
                              border: `1px solid ${theme.palette.primary.border2}`,
                              borderRadius: "10px",
                              p: 1,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: theme.palette.primary.main,
                                bgcolor: `${theme.palette.primary.main}0a`,
                              },
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {modifier.Name}
                            </Typography>
                          </Box>
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No modifiers available
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default InterpretationSendDetails;
